/**
 * LabelPrintPage.jsx
 * TSC TTP-244C termal etiket basma — Şablon editörü + Yazıcı rehberi + Baskı CSS düzeltmesi
 */

import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Printer, Search, Tag, X, Package, Loader2,
  ChevronRight, Filter, Settings2, ChevronDown,
  Palette, HelpCircle, RotateCcw, Eye, EyeOff,
  Check, Image as ImageIcon, Type, AlignLeft, Info,
  Monitor, AlertCircle, CheckCircle2, Truck, Copy, MapPin,
  FileText, Factory, Receipt, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { ModuleIcon } from "../components/layout/Sidebar";
import { NavigationCtx } from "../appContexts";
import { useJspm, jspm } from "../lib/jspm";
import { PrintPreviewModal } from "../components/PrintPreviewModal";

// ─── Etiket Boyutları ──────────────────────────────────────────────────────────
const SCREEN_SC = 3.78;

const PRESET_SIZES = [
  { id: "49x20",  label: "49×20 mm",  w: 49,  h: 20,  pad: 1.4 },
  { id: "58x30",  label: "58×30 mm",  w: 58,  h: 30,  pad: 2.0 },
  { id: "60x40",  label: "60×40 mm",  w: 60,  h: 40,  pad: 2.5 },
  { id: "100x50", label: "100×50 mm", w: 100, h: 50,  pad: 3.0 },
  { id: "custom", label: "Özel...",   w: null, h: null, pad: 2.0 },
];
const DEFAULT_SIZE = PRESET_SIZES[0];

// ─── Font Boyutu Sabitleri (1=küçük, 2=orta, 3=büyük) ─────────────────────────
// TSPL: 1→font"1"(8dot), 2→font"2"(16dot), 3→font"3"(24dot)
const TSPL_FONT = { 1: "1", 2: "2", 3: "3" };
// Ekran önizleme ölçek çarpanı
const FS_SCALE  = { 1: 0.78, 2: 1.00, 3: 1.30 };

// ─── Şablon Varsayılanları ─────────────────────────────────────────────────────
const TMPL_DEFAULTS = {
  logoType:    "text",      // "text" | "image" | "none"
  logoText:    "ZEN",
  logoUrl:     "",
  brandColor:  "#C42821",
  showOrderNo: true,
  showCustomer:true,
  showProduct: true,
  showFabric:  true,
  showDims:    true,
  showAddress: false,
  showQty:     true,
  orientation: "auto",      // "auto" | "portrait" (90° döndür)
  topOffset:   0,           // mm — dikey kaydırma (-5..+5), Chrome margin telafisi
  rowGap:      0.6,         // mm — satırlar arası boşluk
  // Alan bazlı font boyutları (1=küçük/2=orta/3=büyük)
  fontSizes: {
    logoText:  2,
    orderNo:   1,
    customer:  2,
    product:   2,
    fabric:    1,
    dims:      3,
    qty:       2,
    address:   1,
  },
};
const CARGO_TMPL_DEFAULTS = {
  logoType:     "text",
  logoText:     "ZEN",
  showOrderNo:  true,
  showCustomer: true,
  showAddress:  true,
  showCity:     true,
  showPhone:    true,
  showItems:    true,
  showBarcode:  true,
  yOffset:      0,          // mm — tüm satırları kaydır (Y kalibrasyon)
  customerFont: "4",        // "3" | "4" | "5"
  addressFont:  "3",        // "2" | "3"
  cityFont:     "auto",     // "auto" | "3" | "4" — auto: district boşsa "4", doluysa "3"
  // Alan bazlı font boyutları (1=küçük/2=orta/3=büyük)
  fontSizes: {
    logoText:  2,
    orderNo:   1,
    customer:  3,
    address:   2,
    city:      2,
    phone:     1,
    items:     1,
  },
};
const CARGO_TMPL_KEY = "zen_kargo_tmpl";

function loadTmpl() {
  try { return { ...TMPL_DEFAULTS, ...JSON.parse(localStorage.getItem("zen_label_tmpl") || "{}") }; }
  catch { return { ...TMPL_DEFAULTS }; }
}
function saveTmpl(t) {
  try { localStorage.setItem("zen_label_tmpl", JSON.stringify(t)); } catch { /* noop */ }
}
function loadKargoTmpl() {
  try { return { ...CARGO_TMPL_DEFAULTS, ...JSON.parse(localStorage.getItem(CARGO_TMPL_KEY) || "{}") }; }
  catch { return { ...CARGO_TMPL_DEFAULTS }; }
}
function saveKargoTmpl(t) {
  try { localStorage.setItem(CARGO_TMPL_KEY, JSON.stringify(t)); } catch { /* noop */ }
}

// ─── Yardımcı Fonksiyonlar ────────────────────────────────────────────────────
function getOpt(item, keyword) {
  const opts = item.options || [];
  const found = opts.find(o => (o.label || "").toLowerCase().includes(keyword.toLowerCase()));
  return found?.value || null;
}
function getFabric(item) {
  if (item.fabric) return item.fabric;
  if (item.fabricBrand) return item.fabricBrand;
  return getOpt(item, "kumaş") || getOpt(item, "fabric") || "";
}
function getDims(item) {
  const w = item.widthCm || 0;
  const h = item.heightCm || 0;
  if (w && h) return { w, h };
  const opts = item.options || [];
  const wOpt = opts.find(o => { const l = (o.label||"").trim().toLowerCase(); return l === "en" || l.endsWith("/ en"); });
  const hOpt = opts.find(o => { const l = (o.label||"").trim().toLowerCase(); return l === "boy" || l.endsWith("/ boy"); });
  return { w: Number(wOpt?.value)||0, h: Number(hOpt?.value)||0 };
}
function getDirection(item) {
  return getOpt(item, "kablo yönü") || getOpt(item, "montaj yönü") || getOpt(item, "yön") || "";
}
const ono = (o) => o?.orderNo || "";
const esc = (s) => String(s||"").replace(/</g,"&lt;").replace(/>/g,"&gt;");

// ─── Print HTML ────────────────────────────────────────────────────────────────
function buildLabelHTML(items, order, customer, size, tmpl) {
  const { w: LW, h: LH, pad: LP } = size;
  const scale    = LH / 20;
  const topOff   = tmpl.topOffset || 0;   // mm
  const rowGap   = tmpl.rowGap ?? 0.6;    // mm
  const f  = (mm) => `${(mm * scale * 2.835).toFixed(1)}pt`;
  const fmm = (mm) => `${(mm).toFixed(2)}mm`;
  const portrait = tmpl.orientation === "portrait";
  // Portrait modda @page ters, içerik 90° döner
  const pageW = portrait ? LH : LW;
  const pageH = portrait ? LW : LH;

  const addr = customer
    ? [customer.address, customer.city, customer.country].filter(Boolean).join(", ")
    : "";
  const orderNo = ono(order);

  // Logo HTML
  let logoHtml = "";
  if (tmpl.logoType === "text" && tmpl.logoText)
    logoHtml = `<span class="brand">${esc(tmpl.logoText)}</span>`;
  else if (tmpl.logoType === "image" && tmpl.logoUrl)
    logoHtml = `<img src="${esc(tmpl.logoUrl)}" class="logo-img" alt="logo" />`;

  const pages = items.map((item) => {
    const fabric   = getFabric(item);
    const { w, h } = getDims(item);
    const dir      = getDirection(item);
    const dims     = w && h ? `${w} × ${h} cm` : w ? `${w} cm` : "";
    const dirStr   = dir ? ` · ${dir}` : "";

    const rowTop = (tmpl.logoType !== "none" || tmpl.showOrderNo) ? `
  <div class="row-top">
    ${logoHtml}
    ${tmpl.showOrderNo ? `<span class="ono">${esc(orderNo)}</span>` : ""}
  </div>` : "";

    return `
<div class="label">
  ${rowTop}
  ${tmpl.showCustomer ? `<div class="cust">${esc(order.customerName||"")}</div>` : ""}
  ${tmpl.showProduct  ? `<div class="prod">${esc(item.product||"")}${esc(dirStr)}</div>` : ""}
  ${tmpl.showFabric && fabric ? `<div class="fabric">${esc(fabric)}</div>` : ""}
  ${tmpl.showDims || tmpl.showQty ? `
  <div class="row-bot">
    ${tmpl.showDims ? `<span class="dims">${esc(dims)}</span>` : ""}
    ${tmpl.showQty  ? `<span class="qty">Adet: ${item.qty||1}</span>` : ""}
  </div>` : ""}
  ${tmpl.showAddress && addr ? `<div class="addr">${esc(addr)}</div>` : ""}
</div>`;
  }).join("\n");

  const portraitCSS = portrait ? `
.label {
  transform: rotate(-90deg);
  transform-origin: ${LH * 2.835}pt 0;
  margin-top: ${LH * 2.835}pt;
}` : "";

  const topPad    = Math.max(0, LP + topOff).toFixed(2);
  const bottomPad = Math.max(0, LP - topOff).toFixed(2);

  return `<!DOCTYPE html><html lang="tr"><head><meta charset="utf-8">
<title>ZEN Etiket</title>
<style>
@page { size: ${pageW}mm ${pageH}mm; margin: 0 !important; }
*{box-sizing:border-box;margin:0;padding:0;}
html,body{width:${pageW}mm;height:${pageH}mm;overflow:hidden;background:#fff;color:#000;font-family:Arial,Helvetica,sans-serif;}
.label{
  width:${LW}mm;height:${LH}mm;
  padding:${LP}mm;
  padding-top:${topPad}mm;
  padding-bottom:${bottomPad}mm;
  display:flex;flex-direction:column;
  justify-content:center;
  gap:${(rowGap * scale).toFixed(2)}mm;
  overflow:hidden;
  page-break-after:always;break-after:page;
}
.label:last-child{page-break-after:auto;break-after:auto;}
${portraitCSS}
.row-top{display:flex;justify-content:space-between;align-items:center;min-height:0;}
.brand{font-size:${f(2.3)};font-weight:900;color:${tmpl.brandColor};letter-spacing:-0.2pt;}
.logo-img{height:${f(2.8)};max-width:${f(12)};object-fit:contain;}
.ono{font-size:${f(1.8)};color:#555;font-family:monospace;}
.cust{font-size:${f(2.1)};font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.prod{font-size:${f(2.4)};font-weight:900;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.fabric{font-size:${f(1.9)};color:#333;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.row-bot{display:flex;justify-content:space-between;align-items:baseline;min-height:0;}
.dims{font-size:${f(2.6)};font-weight:900;color:#111;}
.qty{font-size:${f(1.8)};color:#555;}
.addr{font-size:${f(1.7)};color:#444;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}
</style>
</head><body>${pages}</body></html>`;
}

function doPrint(items, order, customer, size, tmpl) {
  if (!items.length) { toast.error("Yazdırılacak kalem yok"); return; }
  const html = buildLabelHTML(items, order, customer, size, tmpl);
  const w = window.open("", "_blank", "width=480,height=320,toolbar=no,menubar=no,scrollbars=no");
  if (!w) { toast.error("Pop-up engellendi! Tarayıcı izinlerini kontrol edin."); return; }
  w.document.write(html);
  w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 450);
  toast.success(`${items.length} etiket yazıcıya gönderildi`);
}

// ─── TSPL Komut Üretici (TSC TTP-244C, 203 DPI) ───────────────────────────
const MM_TO_DOTS = 203 / 25.4; // ≈ 7.99 dot/mm
function mmDot(mm) { return Math.round(mm * MM_TO_DOTS); }
function escTSPL(s) {
  // TSC TEXT komutunda çift tırnak yasak → tek tırnak, backslash kaldır
  return String(s || "").replace(/"/g, "'").replace(/\\/g, "").substring(0, 35);
}

function generateTSPL(item, order, customer, size, tmpl, copies = 1) {
  const { w: LW, h: LH } = size;
  const dotW   = mmDot(LW);
  const dotH   = mmDot(LH);
  const fabric = getFabric(item);
  const { w: dimW, h: dimH } = getDims(item);
  const dir    = getDirection(item);
  const dims   = dimW && dimH ? `${dimW} x ${dimH} cm` : dimW ? `${dimW} cm` : "";
  const dirStr = dir ? ` ${dir}` : "";
  const orderNo = ono(order);
  const addr   = customer
    ? [customer.address, customer.city].filter(Boolean).join(", ")
    : "";

  const fs = tmpl.fontSizes || {};
  const tf = (key, def) => TSPL_FONT[fs[key] || def] || TSPL_FONT[def];

  // Aktif satırları belirle
  const rows = [];
  if (tmpl.logoType !== "none" || tmpl.showOrderNo)      rows.push("header");
  if (tmpl.showCustomer && (order?.customerName || ""))   rows.push("customer");
  if (tmpl.showProduct  && (item.product || ""))          rows.push("product");
  if (tmpl.showFabric   && fabric)                        rows.push("fabric");
  if ((tmpl.showDims && dims) || tmpl.showQty)            rows.push("dims");
  if (tmpl.showAddress  && addr)                          rows.push("address");

  const padT  = mmDot(1.5);
  const inner = dotH - padT - mmDot(1.5);
  const rowH  = rows.length > 0 ? Math.floor(inner / rows.length) : 28;

  const lines = [
    `SIZE ${LW} mm, ${LH} mm`,
    `GAP 3 mm, 0 mm`,
    `DIRECTION 0`,
    `CLS`,
  ];

  rows.forEach((row, idx) => {
    const y = padT + idx * rowH;

    if (row === "header") {
      if (tmpl.logoType === "text" && tmpl.logoText)
        lines.push(`TEXT 5, ${y}, "${tf("logoText",2)}", 0, 1, 1, "${escTSPL(tmpl.logoText.toUpperCase())}"`);
      if (tmpl.showOrderNo && orderNo) {
        const rightX = Math.max(dotW - orderNo.length * 8 - 5, mmDot(18));
        lines.push(`TEXT ${rightX}, ${y + 4}, "${tf("orderNo",1)}", 0, 1, 1, "${escTSPL(orderNo)}"`);
      }
    } else if (row === "customer") {
      lines.push(`TEXT 5, ${y}, "${tf("customer",2)}", 0, 1, 1, "${escTSPL(order?.customerName || "")}"`);
    } else if (row === "product") {
      lines.push(`TEXT 5, ${y}, "${tf("product",2)}", 0, 1, 1, "${escTSPL((item.product || "") + dirStr)}"`);
    } else if (row === "fabric") {
      lines.push(`TEXT 5, ${y}, "${tf("fabric",1)}", 0, 1, 1, "${escTSPL(fabric)}"`);
    } else if (row === "dims") {
      if (tmpl.showDims && dims)
        lines.push(`TEXT 5, ${y}, "${tf("dims",3)}", 0, 1, 1, "${escTSPL(dims)}"`);
      if (tmpl.showQty) {
        const qtyText = `x${item.qty || 1}`;
        const qtyX    = Math.max(dotW - qtyText.length * 12 - 5, mmDot(30));
        lines.push(`TEXT ${qtyX}, ${y + 2}, "${tf("qty",2)}", 0, 1, 1, "${escTSPL(qtyText)}"`);
      }
    } else if (row === "address") {
      lines.push(`TEXT 5, ${y}, "${tf("address",1)}", 0, 1, 1, "${escTSPL(addr)}"`);
    }
  });

  lines.push(`PRINT ${copies}, 1`);

  const tsplStr = lines.join("\r\n");
  /* eslint-disable no-console */
  console.log("[ZEN TSPL — " + LW + "x" + LH + "mm, " + copies + " kopya]\n" + tsplStr);
  return tsplStr;
}

// ─── Kargo Etiketi TSPL (100×50mm, GAP 2.60mm, yan boşluk 1.3mm) ─────────
function generateCargoTSPL(order, customer, kargoTmpl, copies = 1) {
  const LW = 100, LH = 50;
  const lp   = mmDot(1.3);
  const dotW = mmDot(LW);
  const yOff = Math.round(mmDot(kargoTmpl?.yOffset || 0)); // kalibrasyon

  // y() — base değere offset ekle (negatif olmaz)
  const y = (base) => Math.max(0, base + yOff);

  const fs = kargoTmpl?.fontSizes || {};
  const tf = (key, def) => TSPL_FONT[fs[key] || def] || TSPL_FONT[def];

  const orderNo  = ono(order);
  const custName = order?.customerName || "";
  const addr     = customer?.address || "";
  const district = customer?.district || "";
  const city     = customer?.city || "";
  const country  = customer?.country || "";
  const cityLine = [district, city, country].filter(Boolean).join(" / ");
  const phone    = customer?.phone || customer?.telephone || "";
  const items    = order?.items || [];

  const dimSummary = items.slice(0, 3).map(it => {
    const { w: dw, h: dh } = getDims(it);
    return dw && dh ? `${dw}x${dh}` : escTSPL((it.product || "").substring(0, 8));
  }).join(" | ");
  const itemLine = `${items.length} kalem${dimSummary ? "  " + dimSummary : ""}`;

  const lines = [
    `SIZE ${LW} mm, ${LH} mm`,
    `GAP 2.60 mm, 0 mm`,
    `DIRECTION 0`,
    `CLS`,
  ];

  // ── Satır 1: Logo sol + Sipariş No sağ ───────────
  if (kargoTmpl?.logoType === "text" && kargoTmpl?.logoText)
    lines.push(`TEXT ${lp}, ${y(12)}, "${tf("logoText",2)}", 0, 1, 1, "${escTSPL(kargoTmpl.logoText.toUpperCase())}"`);
  if (kargoTmpl?.showOrderNo !== false && orderNo) {
    const rx = Math.max(dotW - orderNo.length * 16 - lp, mmDot(50));
    lines.push(`TEXT ${rx}, ${y(14)}, "${tf("orderNo",1)}", 0, 1, 1, "${escTSPL(orderNo)}"`);
  }

  // ── Satır 2: Müşteri Adı (büyük, Font 4) ─────────
  if (kargoTmpl?.showCustomer !== false && custName)
    lines.push(`TEXT ${lp}, ${y(52)}, "${tf("customer",3)}", 0, 1, 1, "${escTSPL(custName)}"`);

  // ── Satır 3: Adres ────────────────────────────────
  if (kargoTmpl?.showAddress !== false && addr)
    lines.push(`TEXT ${lp}, ${y(104)}, "${tf("address",2)}", 0, 1, 1, "${escTSPL(addr)}"`);

  // ── Satır 4: Şehir / İlçe / Ülke ─────────────────
  if (kargoTmpl?.showCity !== false && cityLine)
    lines.push(`TEXT ${lp}, ${y(148)}, "${tf("city",2)}", 0, 1, 1, "${escTSPL(cityLine)}"`);

  // ── Satır 5: Telefon ──────────────────────────────
  if (kargoTmpl?.showPhone !== false && phone)
    lines.push(`TEXT ${lp}, ${y(192)}, "${tf("phone",1)}", 0, 1, 1, "${escTSPL("Tel: " + phone)}"`);

  // ── Satır 6: Ürün özeti ───────────────────────────
  if (kargoTmpl?.showItems !== false && itemLine)
    lines.push(`TEXT ${lp}, ${y(230)}, "${tf("items",1)}", 0, 1, 1, "${escTSPL(itemLine)}"`);

  // ── Ayırıcı çizgi ─────────────────────────────────
  lines.push(`LINE ${lp}, ${y(265)}, ${dotW - lp}, ${y(265)}, 1`);

  // ── Barkod CODE128 (siparişin kodu) ──────────────
  if (kargoTmpl?.showBarcode !== false && orderNo)
    lines.push(`BARCODE ${lp}, ${y(275)}, "128", 60, 0, 0, 2, 3, "${escTSPL(orderNo)}"`);

  lines.push(`PRINT ${copies}, 1`);

  const tsplStr = lines.join("\r\n");
  console.log(`[ZEN TSPL KARGO — 100x50mm, gap=2.60mm, yan=1.3mm, yOff=${kargoTmpl?.yOffset || 0}mm, ${copies} kopya]\n` + tsplStr);
  return tsplStr;
}

// ─── TSPL Kalibrasyon Önizleme Modal ──────────────────────────────────────
function TSPLPreviewModal({ tspl, onClose, onPrint }) {
  const [edited, setEdited] = React.useState(tspl);

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm"
      onClick={onClose}>
      <div className="bg-white dark:bg-[#0F1923] rounded-2xl shadow-2xl border border-slate-200 dark:border-[#22304A] w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-[#22304A]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Printer className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 dark:text-slate-100 text-sm">TSPL Komutları — Kalibrasyon</h3>
              <p className="text-[11px] text-slate-400">Koordinatları düzenleyerek yazıcıya gönderebilirsiniz</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-[#22304A] text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* TSPL Textarea */}
        <div className="p-5 flex-1 overflow-hidden flex flex-col gap-3">
          <textarea
            value={edited}
            onChange={e => setEdited(e.target.value)}
            spellCheck={false}
            className="flex-1 min-h-[280px] w-full font-mono text-xs p-3 bg-slate-50 dark:bg-[#0A1628] rounded-xl border border-slate-200 dark:border-[#22304A] resize-none focus:outline-none focus:ring-2 focus:ring-[#E5322A]/30 text-slate-700 dark:text-slate-300 leading-relaxed"
            data-testid="tspl-preview-textarea"
          />
          <div className="flex items-center gap-2 justify-between">
            <p className="text-[10px] text-slate-400 leading-relaxed">
              💡 <strong>TEXT x, y, "font", 0, 1, 1, "metin"</strong> — x/y değerlerini değiştirerek konumu kalibre edin (203 DPI: 1mm = ~8 dot)
            </p>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => { navigator.clipboard?.writeText(edited).then(() => toast.success("TSPL kopyalandı")); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-[#22304A] text-xs font-semibold text-slate-600 dark:text-slate-300 hover:border-[#E5322A]/40 transition-all"
                data-testid="tspl-copy-btn">
                <Copy className="w-3.5 h-3.5" /> Kopyala
              </button>
              {onPrint && (
                <button
                  onClick={() => { onPrint(edited); onClose(); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E5322A] text-white text-xs font-semibold hover:bg-[#C42821] transition-all"
                  data-testid="tspl-send-btn">
                  <Printer className="w-3.5 h-3.5" /> Bu TSPL ile Bas
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Adet / Kopya Seçici ──────────────────────────────────────────────────
function CopySelector({ value, onDec, onInc, testId }) {
  return (
    <div className="flex items-stretch border border-slate-200 dark:border-[#22304A] rounded-lg overflow-hidden shrink-0 bg-white dark:bg-[#141E30]"
      data-testid={testId || "copy-selector"}>
      <button onClick={onDec} disabled={value <= 1}
        className="px-2.5 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[#1A2540] disabled:opacity-30 font-bold text-sm transition-all select-none">
        −
      </button>
      <span className="px-2 py-1.5 text-xs font-bold text-slate-700 dark:text-slate-200 min-w-[28px] text-center flex items-center justify-center border-x border-slate-200 dark:border-[#22304A]">
        {value}
      </span>
      <button onClick={onInc} disabled={value >= 99}
        className="px-2.5 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[#1A2540] disabled:opacity-30 font-bold text-sm transition-all select-none">
        +
      </button>
    </div>
  );
}

// ─── Yazıcı Seçici (JSPM Bağlantı Göstergesi + Dropdown) ─────────────────
function PrinterSelector({ connected, connecting, printers, selectedPrinter, onSelect }) {
  return (
    <div className="flex items-center gap-2" data-testid="jspm-printer-selector">
      {/* Bağlantı badge */}
      <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold select-none transition-colors ${
        connected    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
        : connecting ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
        : "bg-slate-100 text-slate-500 dark:bg-[#1A2540] dark:text-slate-400"
      }`}>
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
          connected ? "bg-emerald-500" : connecting ? "bg-amber-500 animate-pulse" : "bg-slate-400"
        }`} />
        {connecting ? "JSPM..." : connected ? "JSPM Bağlı" : "JSPM Yok"}
      </span>

      {/* Yazıcı dropdown — sadece bağlıyken */}
      {connected && printers.length > 0 && (
        <select
          value={selectedPrinter}
          onChange={e => onSelect(e.target.value)}
          className="zen-input !py-1 !text-xs max-w-[180px]"
          data-testid="jspm-printer-select"
        >
          <option value="">Yazıcı seç...</option>
          {printers.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      )}
      {connected && printers.length === 0 && (
        <span className="text-xs text-slate-400 italic">Yazıcı bulunamadı</span>
      )}
    </div>
  );
}

// ─── Etiket Önizleme (Ekran) ──────────────────────────────────────────────────
function LabelPreview({ item, order, customer, size, tmpl }) {
  const { w: LW, h: LH, pad: LP } = size;
  const previewMaxW = 260;
  const SC = Math.min(SCREEN_SC, previewMaxW / LW);
  const W = LW * SC, H = LH * SC, PAD = LP * SC;
  const scale = LH / 20;
  // base font size, multiplied by FS_SCALE factor per field
  const fs = tmpl.fontSizes || {};
  const fbase = (mm) => mm * scale * SC * 0.78;
  const f = (mm, key, def = 2) => `${(fbase(mm) * (FS_SCALE[fs[key] ?? def] || 1)).toFixed(1)}px`;

  const fabric   = getFabric(item);
  const { w, h } = getDims(item);
  const dir      = getDirection(item);
  const dims     = w && h ? `${w} × ${h} cm` : w ? `${w} cm` : "";
  const addr     = customer ? [customer.address, customer.city, customer.country].filter(Boolean).join(", ") : "";

  return (
    <div
      style={{
        width: W, height: H,
        padding: PAD,
        paddingTop:    Math.max(0, (LP + (tmpl.topOffset || 0))) * SC,
        paddingBottom: Math.max(0, (LP - (tmpl.topOffset || 0))) * SC,
        border: "1.5px solid #e5e7eb", borderRadius: 3,
        background: "#fff", color: "#000", fontFamily: "Arial, Helvetica, sans-serif",
        display: "flex", flexDirection: "column",
        justifyContent: "center",
        gap: (tmpl.rowGap ?? 0.6) * scale * SC * 0.78,
        overflow: "hidden", flexShrink: 0, boxShadow: "0 2px 12px rgba(0,0,0,0.18)"
      }}
      data-testid="label-preview"
    >
      {(tmpl.logoType !== "none" || tmpl.showOrderNo) && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: 0 }}>
          {tmpl.logoType === "text" && tmpl.logoText && (
            <span style={{ fontSize: f(2.3,"logoText"), fontWeight: 900, color: tmpl.brandColor, letterSpacing: -0.2 }}>{tmpl.logoText}</span>
          )}
          {tmpl.logoType === "image" && tmpl.logoUrl && (
            <img src={tmpl.logoUrl} alt="logo" style={{ height: f(2.8,"logoText"), maxWidth: f(12,"logoText"), objectFit: "contain" }} />
          )}
          {tmpl.logoType === "none" && <span />}
          {tmpl.showOrderNo && <span style={{ fontSize: f(1.8,"orderNo",1), color: "#666", fontFamily: "monospace" }}>{ono(order)}</span>}
        </div>
      )}
      {tmpl.showCustomer && (
        <div style={{ fontSize: f(2.1,"customer"), fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {order.customerName || ""}
        </div>
      )}
      {tmpl.showProduct && (
        <div style={{ fontSize: f(2.4,"product"), fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.product || ""}{dir ? ` · ${dir}` : ""}
        </div>
      )}
      {tmpl.showFabric && fabric && (
        <div style={{ fontSize: f(1.9,"fabric",1), color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fabric}</div>
      )}
      {(tmpl.showDims || tmpl.showQty) && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", minHeight: 0 }}>
          {tmpl.showDims && <span style={{ fontSize: f(2.6,"dims",3), fontWeight: 900, color: "#111" }}>{dims}</span>}
          {tmpl.showQty  && <span style={{ fontSize: f(1.8,"qty"), color: "#555" }}>×{item.qty || 1}</span>}
        </div>
      )}
      {tmpl.showAddress && addr && (
        <div style={{ fontSize: f(1.7,"address",1), color: "#555", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{addr}</div>
      )}
    </div>
  );
}

// ─── Boyut Seçici ──────────────────────────────────────────────────────────────
function SizeSelector({ size, customW, customH, onSelect, onCustomChange }) {
  const [open, setOpen] = useState(false);
  const activePreset = PRESET_SIZES.find(p => p.id !== "custom" && p.w === size.w && p.h === size.h);
  const isCustom = !activePreset;
  const displayLabel = isCustom ? `${size.w}×${size.h} mm` : (activePreset?.label || "Boyut");

  return (
    <div className="relative" data-testid="label-size-selector">
      <button onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#141E30] text-sm font-semibold text-slate-700 dark:text-slate-200 hover:border-[#E5322A]/50 transition-all">
        <Settings2 className="w-4 h-4 text-slate-400" />
        {displayLabel}
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-white dark:bg-[#141E30] border border-slate-200 dark:border-[#22304A] rounded-xl shadow-xl w-60 py-2">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-3 pb-1.5">Etiket Boyutu</p>
          {PRESET_SIZES.filter(p => p.id !== "custom").map(p => {
            const active = !isCustom && activePreset?.id === p.id;
            return (
              <button key={p.id} data-testid={`size-option-${p.id}`}
                onClick={() => { onSelect(p); setOpen(false); }}
                className={`w-full text-left px-3 py-2 flex items-center justify-between transition-colors ${active ? "bg-[#E5322A]/8 text-[#E5322A]" : "hover:bg-slate-50 dark:hover:bg-[#1A2540] text-slate-700 dark:text-slate-200"}`}>
                <span className="text-sm font-semibold">{p.label}</span>
                {active && <Check className="w-3.5 h-3.5" />}
              </button>
            );
          })}
          <div className="border-t border-slate-100 dark:border-[#22304A] mt-1 pt-2 px-3">
            <p className="text-[10px] font-semibold text-slate-400 mb-2">Özel Boyut (mm)</p>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <label className="text-[10px] text-slate-400 block mb-1">Genişlik</label>
                <input type="number" min="20" max="200" value={customW} onChange={e => onCustomChange("w", e.target.value)} className="zen-input !py-1 !text-sm text-center w-full" data-testid="custom-size-w" />
              </div>
              <span className="text-slate-400 mt-4">×</span>
              <div className="flex-1">
                <label className="text-[10px] text-slate-400 block mb-1">Yükseklik</label>
                <input type="number" min="10" max="200" value={customH} onChange={e => onCustomChange("h", e.target.value)} className="zen-input !py-1 !text-sm text-center w-full" data-testid="custom-size-h" />
              </div>
            </div>
            <button data-testid="apply-custom-size-btn" onClick={() => {
              const w = Math.max(20, Math.min(200, Number(customW) || 49));
              const h = Math.max(10, Math.min(200, Number(customH) || 20));
              onSelect({ id: "custom", label: `${w}×${h} mm`, w, h, pad: Math.max(1, h * 0.07) });
              setOpen(false);
            }} className="mt-2 w-full zen-btn-primary !py-1.5 text-sm">Uygula</button>
          </div>
        </div>
      )}
      {open && <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />}
    </div>
  );
}

// ─── Manuel Etiket Modali ─────────────────────────────────────────────────────
function ManualLabelModal({ onClose, onPrint }) {
  const [form, setForm] = React.useState({
    product:      "",
    widthCm:      "",
    heightCm:     "",
    customerName: "",
    orderNo:      "",
    fabric:       "",
    qty:          1,
    direction:    "",
  });
  const [copies, setCopies] = React.useState(1);
  const sf = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = () => {
    if (!form.product && !form.widthCm && !form.customerName) {
      toast.error("En az bir alan doldurun (ürün adı, ölçü veya müşteri)");
      return;
    }
    // Sahte item + order nesnesi oluştur
    const mockItem = {
      id:       "MANUEL",
      product:  form.product || "—",
      widthCm:  Number(form.widthCm) || 0,
      heightCm: Number(form.heightCm) || 0,
      qty:      Number(form.qty) || 1,
      fabric:   form.fabric || "",
      options:  form.direction
        ? [{ label: "Kablo Yönü", value: form.direction }]
        : [],
    };
    const mockOrder = {
      customerName: form.customerName || "—",
      orderNo:      form.orderNo || "MANUEL",
    };
    onPrint(mockItem, mockOrder, copies);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white dark:bg-[#0F1923] rounded-2xl shadow-2xl border border-slate-200 dark:border-[#22304A] w-full max-w-md flex flex-col"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-[#22304A]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#E5322A] to-orange-600 flex items-center justify-center">
              <Tag className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 dark:text-slate-100 text-sm">Manuel Etiket</h3>
              <p className="text-[11px] text-slate-400">Sipariş olmadan özel etiket bas</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-[#22304A] text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <div className="p-5 space-y-4">
          {/* Ürün Adı */}
          <div>
            <label className="zen-label mb-1">Ürün Adı</label>
            <input
              value={form.product}
              onChange={e => sf("product", e.target.value)}
              placeholder="Dikey Zip, Stor Perde..."
              className="zen-input text-sm w-full"
              data-testid="manual-label-product"
            />
          </div>

          {/* Ölçüler */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="zen-label mb-1">Genişlik (cm)</label>
              <input
                type="number" min="0" max="1000"
                value={form.widthCm}
                onChange={e => sf("widthCm", e.target.value)}
                placeholder="120"
                className="zen-input text-sm w-full"
                data-testid="manual-label-width"
              />
            </div>
            <div>
              <label className="zen-label mb-1">Yükseklik (cm)</label>
              <input
                type="number" min="0" max="1000"
                value={form.heightCm}
                onChange={e => sf("heightCm", e.target.value)}
                placeholder="240"
                className="zen-input text-sm w-full"
                data-testid="manual-label-height"
              />
            </div>
          </div>

          {/* Müşteri + Sipariş No */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="zen-label mb-1">Müşteri Adı</label>
              <input
                value={form.customerName}
                onChange={e => sf("customerName", e.target.value)}
                placeholder="Ahmet Bey"
                className="zen-input text-sm w-full"
                data-testid="manual-label-customer"
              />
            </div>
            <div>
              <label className="zen-label mb-1">Sipariş No (isteğe bağlı)</label>
              <input
                value={form.orderNo}
                onChange={e => sf("orderNo", e.target.value)}
                placeholder="#001"
                className="zen-input text-sm w-full"
                data-testid="manual-label-orderno"
              />
            </div>
          </div>

          {/* Kumaş + Yön */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="zen-label mb-1">Kumaş / Kod</label>
              <input
                value={form.fabric}
                onChange={e => sf("fabric", e.target.value)}
                placeholder="Pearl 01"
                className="zen-input text-sm w-full"
                data-testid="manual-label-fabric"
              />
            </div>
            <div>
              <label className="zen-label mb-1">Yön (isteğe bağlı)</label>
              <input
                value={form.direction}
                onChange={e => sf("direction", e.target.value)}
                placeholder="Sağ / Sol"
                className="zen-input text-sm w-full"
                data-testid="manual-label-direction"
              />
            </div>
          </div>

          {/* Adet + Kopya */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="zen-label mb-1">Ürün Adeti</label>
              <input
                type="number" min="1" max="999"
                value={form.qty}
                onChange={e => sf("qty", e.target.value)}
                className="zen-input text-sm w-full"
                data-testid="manual-label-qty"
              />
            </div>
            <div>
              <label className="zen-label mb-1">Baskı Kopyası</label>
              <div className="flex items-stretch border border-slate-200 dark:border-[#22304A] rounded-lg overflow-hidden bg-white dark:bg-[#141E30] h-[38px]">
                <button onClick={() => setCopies(v => Math.max(1, v - 1))} disabled={copies <= 1}
                  className="px-3 text-slate-500 hover:bg-slate-50 dark:hover:bg-[#1A2540] disabled:opacity-30 font-bold transition-all select-none">−</button>
                <span className="flex-1 flex items-center justify-center text-sm font-bold text-slate-700 dark:text-slate-200 border-x border-slate-200 dark:border-[#22304A]">{copies}</span>
                <button onClick={() => setCopies(v => Math.min(99, v + 1))} disabled={copies >= 99}
                  className="px-3 text-slate-500 hover:bg-slate-50 dark:hover:bg-[#1A2540] disabled:opacity-30 font-bold transition-all select-none">+</button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-[#22304A] text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#1A2540] transition-all">
            İptal
          </button>
          <button onClick={handleSubmit} data-testid="manual-label-submit-btn"
            className="flex-1 zen-btn-primary flex items-center justify-center gap-2 !py-2.5">
            <Printer className="w-4 h-4" />
            Etiket Bas ({copies} kopya)
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Yazıcı Kurulum Rehberi Modal ─────────────────────────────────────────────
function PrinterGuide({ onClose }) {
  const steps = [
    {
      step: 1,
      title: "Windows Yazıcı Sürücüsü",
      icon: <Monitor className="w-5 h-5 text-blue-500" />,
      color: "blue",
      items: [
        "Başlat → Ayarlar → Bluetooth ve cihazlar → Yazıcılar",
        "TSC TTP-244C'ye sağ tık → Yazıcı özellikleri",
        "\"Gelişmiş\" sekmesi → Kağıt boyutu → \"Yeni Ekle\"",
        "Genişlik: 49mm / Yükseklik: 20mm gir (kullandığınız boyuta göre)",
        "Tamam ile kaydet",
      ],
    },
    {
      step: 2,
      title: "Tarayıcı Yazdırma Ayarları",
      icon: <Printer className="w-5 h-5 text-emerald-500" />,
      color: "emerald",
      items: [
        "\"Bas\" butonuna tıklayın → yeni pencere açılır",
        "Ctrl+P ile yazdırma diyaloğunu açın",
        "Yazıcı: TSC TTP-244C seçin",
        "⚠️ Kenar Boşluğu → \"Yok\" (None) seçin",
        "⚠️ Ölçek → %100 olarak bırakın (Scale: 100%)",
        "Üstbilgi/Altbilgi → Kaldır (devre dışı bırak)",
        "Kağıt boyutu → Yukarıda ayarladığınız özel boyutu seçin",
      ],
    },
    {
      step: 3,
      title: "Yazı Karışık / Döndürülmüş Çıkıyor",
      icon: <RotateCcw className="w-5 h-5 text-amber-500" />,
      color: "amber",
      items: [
        "Şablon Editörü → \"Yönlendirme\" → \"Dikey (90° Döndür)\" seçin",
        "Önizlemede etiket dik görünür — bu normaldir, çıktı doğru çıkar",
        "Hâlâ yanlışsa: Yazıcı sürücüsünde Orientation → Landscape deneyin",
        "TSC Bartender veya TSC Console ile test baskısı yapın",
      ],
    },
    {
      step: 4,
      title: "Hâlâ Sorun Varsa",
      icon: <AlertCircle className="w-5 h-5 text-red-500" />,
      color: "red",
      items: [
        "Chrome yerine Edge veya Firefox deneyin",
        "TSC'nin resmi \"TSC Console\" uygulamasından test edin",
        "Yazıcı sürücüsünü kaldırıp yeniden yükleyin",
        "Label Stock Type → Continuous veya Gap/Mark ayarını kontrol edin (3.67mm boşluk)",
      ],
    },
  ];

  const colorMap = {
    blue:    "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800",
    emerald: "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800",
    amber:   "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800",
    red:     "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800",
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white dark:bg-[#0F1923] rounded-2xl shadow-2xl border border-slate-200 dark:border-[#22304A] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-[#0F1923] border-b border-slate-200 dark:border-[#22304A] px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 dark:text-slate-100">TSC TTP-244C Yazıcı Rehberi</h3>
              <p className="text-xs text-slate-400">Termal etiket yazıcısını doğru kurmak için adım adım</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-[#22304A] text-slate-400 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Steps */}
        <div className="p-6 space-y-4">
          {steps.map(s => (
            <div key={s.step} className={`rounded-xl border p-4 ${colorMap[s.color]}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-white/80 dark:bg-black/20 flex items-center justify-center text-xs font-bold text-slate-600">
                  {s.step}
                </div>
                {s.icon}
                <span className="font-bold text-slate-700 dark:text-slate-200">{s.title}</span>
              </div>
              <ul className="space-y-1.5">
                {s.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-500 shrink-0" />
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Hızlı Test */}
          <div className="rounded-xl bg-[#E5322A]/5 border border-[#E5322A]/20 p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-[#E5322A]" />
              <span className="font-bold text-[#E5322A]">Hızlı Test</span>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Soldan herhangi bir sipariş seçin → İlk kalemin <strong>"Bas"</strong> butonuna tıklayın → 
              Açılan pencerede <strong>Ctrl+P</strong> → TSC seçin → Kenar boşluğu Yok → Yazdır.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Şablon Editörü Çekmecesi ──────────────────────────────────────────────────
function TemplateDrawer({ tmpl, onChange, onClose, sampleItem, sampleOrder, labelMode }) {
  const set = (key, val) => {
    const next = { ...tmpl, [key]: val };
    onChange(next); // ebeveyn hem setState hem save yapar
  };

  const setFontSize = (field, val) => {
    const next = { ...tmpl, fontSizes: { ...(tmpl.fontSizes || {}), [field]: val } };
    onChange(next);
  };

  // ─── Toggle Satırı (düzeltilmiş: w-full + transition-colors) ─────────────
  const ToggleRow = ({ label, field, icon }) => (
    <div className="w-full flex items-center justify-between py-2 border-b border-slate-100 dark:border-[#22304A] last:border-0">
      <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 min-w-0">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <button
        data-testid={`tmpl-toggle-${field}`}
        onClick={() => set(field, !tmpl[field])}
        className={`w-10 h-5 rounded-full transition-colors shrink-0 relative ml-3 ${tmpl[field] ? "bg-[#E5322A]" : "bg-slate-200 dark:bg-[#22304A]"}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${tmpl[field] ? "translate-x-5" : "translate-x-0"}`} />
      </button>
    </div>
  );

  // ─── Font Boyutu Satırı (S/M/L butonları) ────────────────────────────────
  const FontSizeRow = ({ label, field, icon }) => {
    const cur = (tmpl.fontSizes || {})[field] ?? 2;
    return (
      <div className="w-full flex items-center justify-between py-1.5 border-b border-slate-100 dark:border-[#22304A] last:border-0">
        <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 min-w-0">
          {icon}
          <span className="truncate">{label}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-2">
          {[1, 2, 3].map(sz => (
            <button key={sz}
              data-testid={`tmpl-fs-${field}-${sz}`}
              onClick={() => setFontSize(field, sz)}
              className={`w-6 h-6 rounded text-[10px] font-bold transition-all ${
                cur === sz
                  ? "bg-[#E5322A] text-white shadow-sm"
                  : "bg-slate-100 dark:bg-[#1A2540] text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-[#22304A]"
              }`}>
              {sz === 1 ? "S" : sz === 2 ? "M" : "L"}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const isKargo = labelMode === "kargo";
  const defaults = isKargo ? CARGO_TMPL_DEFAULTS : TMPL_DEFAULTS;

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" onClick={onClose}>
      <div className="bg-white dark:bg-[#0F1923] w-80 h-full shadow-2xl border-l border-slate-200 dark:border-[#22304A] overflow-y-auto flex flex-col"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-[#0F1923] border-b border-slate-200 dark:border-[#22304A] px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-[#E5322A]" />
            <span className="font-bold text-slate-800 dark:text-slate-100">Şablon Editörü</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => { onChange({...defaults}); }} title="Sıfırla"
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-[#22304A] text-slate-400 transition-colors text-xs">
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-[#22304A] text-slate-400 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Canlı Önizleme — sadece ürün modunda */}
        {!isKargo && sampleItem && sampleOrder && (
          <div className="px-4 py-3 bg-slate-50 dark:bg-[#0A1220] border-b border-slate-200 dark:border-[#22304A] flex flex-col items-center gap-1.5">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Canlı Önizleme</p>
            <LabelPreview
              item={sampleItem}
              order={sampleOrder}
              customer={null}
              size={PRESET_SIZES[0]}
              tmpl={tmpl}
            />
          </div>
        )}

        <div className="flex-1 p-4 space-y-5">
          {/* Logo Tipi */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Logo / Marka</p>
            <div className="flex gap-1 mb-3">
              {[
                { id: "text",  icon: <Type className="w-3.5 h-3.5" />,      label: "Metin" },
                { id: "image", icon: <ImageIcon className="w-3.5 h-3.5" />, label: "Görsel" },
                { id: "none",  icon: <EyeOff className="w-3.5 h-3.5" />,    label: "Gizle" },
              ].map(opt => (
                <button key={opt.id} data-testid={`tmpl-logo-${opt.id}`}
                  onClick={() => set("logoType", opt.id)}
                  className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-lg border text-xs font-semibold transition-all ${
                    tmpl.logoType === opt.id ? "border-[#E5322A] bg-[#E5322A]/8 text-[#E5322A]" : "border-slate-200 dark:border-[#22304A] text-slate-500 hover:border-[#E5322A]/40"}`}>
                  {opt.icon}{opt.label}
                </button>
              ))}
            </div>

            {tmpl.logoType === "text" && (
              <input
                value={tmpl.logoText}
                onChange={e => set("logoText", e.target.value)}
                placeholder="Logo metni (ZEN, Şirket Adı...)"
                className="zen-input text-sm w-full"
                data-testid="tmpl-logo-text-input"
                maxLength={20}
              />
            )}
            {tmpl.logoType === "image" && (
              <input
                value={tmpl.logoUrl}
                onChange={e => set("logoUrl", e.target.value)}
                placeholder="Görsel URL'si (https://...)"
                className="zen-input text-sm w-full"
                data-testid="tmpl-logo-url"
              />
            )}
          </div>

          {/* Renk */}
          {/* ── Marka Rengi (sadece ürün modunda) */}
          {!isKargo && (
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Marka Rengi</p>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={tmpl.brandColor}
                onChange={e => set("brandColor", e.target.value)}
                className="w-10 h-10 rounded-lg border border-slate-200 dark:border-[#22304A] cursor-pointer p-0.5 bg-white"
                data-testid="tmpl-brand-color"
              />
              <div className="flex gap-1.5 flex-wrap">
                {["#C42821","#1D4ED8","#059669","#7C3AED","#D97706","#000000"].map(c => (
                  <button key={c} onClick={() => set("brandColor", c)} title={c}
                    className={`w-6 h-6 rounded-full border-2 transition-all ${tmpl.brandColor === c ? "border-slate-800 scale-110" : "border-transparent hover:scale-105"}`}
                    style={{ background: c }} />
                ))}
              </div>
              <span className="text-xs text-slate-400 font-mono">{tmpl.brandColor}</span>
            </div>
          </div>
          )}

          {/* ── Alan Görünürlüğü — ÜRÜN MODU */}
          {!isKargo && (
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Görünen Alanlar</p>
            <div className="rounded-xl border border-slate-100 dark:border-[#22304A] px-3">
              <ToggleRow label="Sipariş No"    field="showOrderNo"  icon={<AlignLeft className="w-3.5 h-3.5 text-slate-400" />} />
              <ToggleRow label="Müşteri Adı"   field="showCustomer" icon={<AlignLeft className="w-3.5 h-3.5 text-slate-400" />} />
              <ToggleRow label="Ürün Adı"      field="showProduct"  icon={<AlignLeft className="w-3.5 h-3.5 text-slate-400" />} />
              <ToggleRow label="Kumaş / Kod"   field="showFabric"   icon={<AlignLeft className="w-3.5 h-3.5 text-slate-400" />} />
              <ToggleRow label="Ölçüler"       field="showDims"     icon={<AlignLeft className="w-3.5 h-3.5 text-slate-400" />} />
              <ToggleRow label="Adet"          field="showQty"      icon={<AlignLeft className="w-3.5 h-3.5 text-slate-400" />} />
              <ToggleRow label="Adres"         field="showAddress"  icon={<AlignLeft className="w-3.5 h-3.5 text-slate-400" />} />
            </div>
          </div>
          )}

          {/* ── Font Boyutları — ÜRÜN MODU */}
          {!isKargo && (
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Yazı Boyutları <span className="normal-case font-normal text-slate-300">(S / M / L)</span></p>
            <div className="rounded-xl border border-slate-100 dark:border-[#22304A] px-3">
              <FontSizeRow label="Logo / Marka" field="logoText" icon={<Type className="w-3 h-3 text-slate-400" />} />
              <FontSizeRow label="Sipariş No"   field="orderNo"  icon={<AlignLeft className="w-3 h-3 text-slate-400" />} />
              <FontSizeRow label="Müşteri Adı"  field="customer" icon={<AlignLeft className="w-3 h-3 text-slate-400" />} />
              <FontSizeRow label="Ürün Adı"     field="product"  icon={<AlignLeft className="w-3 h-3 text-slate-400" />} />
              <FontSizeRow label="Kumaş"        field="fabric"   icon={<AlignLeft className="w-3 h-3 text-slate-400" />} />
              <FontSizeRow label="Ölçüler"      field="dims"     icon={<AlignLeft className="w-3 h-3 text-slate-400" />} />
              <FontSizeRow label="Adet"         field="qty"      icon={<AlignLeft className="w-3 h-3 text-slate-400" />} />
              <FontSizeRow label="Adres"        field="address"  icon={<MapPin className="w-3 h-3 text-slate-400" />} />
            </div>
          </div>
          )}

          {/* ── Alan Görünürlüğü — KARGO MODU */}
          {isKargo && (
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Kargo Etiket Alanları</p>
            <div className="rounded-xl border border-slate-100 dark:border-[#22304A] px-3">
              <ToggleRow label="Sipariş No"   field="showOrderNo"  icon={<AlignLeft className="w-3.5 h-3.5 text-slate-400" />} />
              <ToggleRow label="Müşteri Adı"  field="showCustomer" icon={<AlignLeft className="w-3.5 h-3.5 text-slate-400" />} />
              <ToggleRow label="Adres"        field="showAddress"  icon={<MapPin className="w-3.5 h-3.5 text-slate-400" />} />
              <ToggleRow label="Şehir / İlçe" field="showCity"     icon={<MapPin className="w-3.5 h-3.5 text-slate-400" />} />
              <ToggleRow label="Telefon"      field="showPhone"    icon={<AlignLeft className="w-3.5 h-3.5 text-slate-400" />} />
              <ToggleRow label="Kalem Özeti"  field="showItems"    icon={<Package className="w-3.5 h-3.5 text-slate-400" />} />
              <ToggleRow label="Barkod (CODE128)" field="showBarcode" icon={<AlignLeft className="w-3.5 h-3.5 text-slate-400" />} />
            </div>
          </div>
          )}

          {/* ── Font Boyutları — KARGO MODU */}
          {isKargo && (
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Yazı Boyutları <span className="normal-case font-normal text-slate-300">(S / M / L)</span></p>
            <div className="rounded-xl border border-slate-100 dark:border-[#22304A] px-3">
              <FontSizeRow label="Logo / Marka" field="logoText"  icon={<Type className="w-3 h-3 text-slate-400" />} />
              <FontSizeRow label="Sipariş No"   field="orderNo"   icon={<AlignLeft className="w-3 h-3 text-slate-400" />} />
              <FontSizeRow label="Müşteri Adı"  field="customer"  icon={<AlignLeft className="w-3 h-3 text-slate-400" />} />
              <FontSizeRow label="Adres"        field="address"   icon={<MapPin className="w-3 h-3 text-slate-400" />} />
              <FontSizeRow label="Şehir / İlçe" field="city"      icon={<MapPin className="w-3 h-3 text-slate-400" />} />
              <FontSizeRow label="Telefon"      field="phone"     icon={<AlignLeft className="w-3 h-3 text-slate-400" />} />
              <FontSizeRow label="Kalem Özeti"  field="items"     icon={<Package className="w-3 h-3 text-slate-400" />} />
            </div>
          </div>
          )}

          {/* ── Yönlendirme (sadece ürün modunda) */}
          {!isKargo && (
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Baskı Yönlendirmesi</p>
            <div className="rounded-xl border border-slate-100 dark:border-[#22304A] overflow-hidden">
              {[
                { id: "auto",     label: "Yatay (Landscape)", sub: "Çoğu TSC kurulumu için" },
                { id: "portrait", label: "Dikey (90° Döndür)", sub: "Yazı ters çıkıyorsa deneyin" },
              ].map(opt => (
                <button key={opt.id} data-testid={`tmpl-orient-${opt.id}`}
                  onClick={() => set("orientation", opt.id)}
                  className={`w-full text-left px-3 py-2.5 flex items-center justify-between border-b border-slate-100 dark:border-[#22304A] last:border-0 transition-colors ${
                    tmpl.orientation === opt.id ? "bg-[#E5322A]/8" : "hover:bg-slate-50 dark:hover:bg-[#1A2540]"}`}>
                  <div>
                    <p className={`text-sm font-semibold ${tmpl.orientation === opt.id ? "text-[#E5322A]" : "text-slate-700 dark:text-slate-200"}`}>{opt.label}</p>
                    <p className="text-[10px] text-slate-400">{opt.sub}</p>
                  </div>
                  {tmpl.orientation === opt.id && <Check className="w-4 h-4 text-[#E5322A]" />}
                </button>
              ))}
            </div>
          </div>
          )}

          {/* ── Konum Ayarı — ÜRÜN MODU (Chrome margin telafisi) */}
          {!isKargo && (
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Konum Ayarı
              <span className="normal-case font-normal ml-1 text-slate-300">(Chrome üst boşluk telafisi)</span>
            </p>
            <div className="space-y-3 rounded-xl border border-slate-100 dark:border-[#22304A] p-3">
              {/* Dikey Kaydırma */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-slate-600 dark:text-slate-300">Dikey Kaydırma</label>
                  <span className="text-xs font-mono font-bold text-[#E5322A] bg-[#E5322A]/8 px-1.5 py-0.5 rounded">
                    {(tmpl.topOffset || 0) >= 0 ? "+" : ""}{(tmpl.topOffset || 0).toFixed(1)} mm
                  </span>
                </div>
                <input
                  type="range"
                  min="-5" max="5" step="0.5"
                  value={tmpl.topOffset || 0}
                  onChange={e => set("topOffset", parseFloat(e.target.value))}
                  data-testid="tmpl-top-offset"
                  className="w-full accent-[#E5322A] cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                  <span>-5mm (yukarı)</span>
                  <span>0</span>
                  <span>+5mm (aşağı)</span>
                </div>
              </div>
              {/* Satır Aralığı */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-slate-600 dark:text-slate-300">Satır Aralığı</label>
                  <span className="text-xs font-mono font-bold text-slate-500 bg-slate-100 dark:bg-[#22304A] px-1.5 py-0.5 rounded">
                    {(tmpl.rowGap ?? 0.6).toFixed(1)} mm
                  </span>
                </div>
                <input
                  type="range"
                  min="0" max="3" step="0.2"
                  value={tmpl.rowGap ?? 0.6}
                  onChange={e => set("rowGap", parseFloat(e.target.value))}
                  data-testid="tmpl-row-gap"
                  className="w-full accent-[#E5322A] cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                  <span>Sıkışık</span>
                  <span>Normal</span>
                  <span>Geniş</span>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 leading-relaxed">
                💡 Üstte boşluk varsa "Dikey Kaydırma"yı <strong>eksi</strong> yönde kaydırın.
                Önizleme anlık güncellenir.
              </p>
            </div>
          </div>
          )} {/* end !isKargo konum ayarı */}

          {/* ── Y Kalibrasyon — KARGO MODU */}
          {isKargo && (
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Y Kalibrasyon
              <span className="normal-case font-normal ml-1 text-slate-300">(TSPL satır kaydırma)</span>
            </p>
            <div className="rounded-xl border border-slate-100 dark:border-[#22304A] p-3 space-y-1">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs text-slate-600 dark:text-slate-300">Dikey Offset</label>
                <span className="text-xs font-mono font-bold text-[#E5322A] bg-[#E5322A]/8 px-1.5 py-0.5 rounded">
                  {(tmpl.yOffset || 0) >= 0 ? "+" : ""}{tmpl.yOffset || 0} mm
                </span>
              </div>
              <input
                type="range" min="-10" max="10" step="1"
                value={tmpl.yOffset || 0}
                onChange={e => set("yOffset", parseInt(e.target.value))}
                data-testid="tmpl-kargo-yoffset"
                className="w-full accent-[#E5322A] cursor-pointer"
              />
              <div className="flex justify-between text-[10px] text-slate-400">
                <span>-10mm (yukarı)</span><span>0</span><span>+10mm (aşağı)</span>
              </div>
              <p className="text-[10px] text-slate-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2 leading-relaxed mt-1">
                💡 Etiket üstten başlıyorsa pozitif, altta kalıyorsa negatif kaydırın.
                "TSPL Gör" ile koordinatları doğrulayın.
              </p>
            </div>
          </div>
          )}

          {/* Ayarlar kaydediliyor notu */}
          <p className="text-[10px] text-slate-400 text-center">
            Ayarlar otomatik kaydediliyor (tarayıcı hafızası)
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Sipariş Kartı ─────────────────────────────────────────────────────────────
function OrderCard({ order, selected, onClick }) {
  const itemCount = (order.items || []).length;
  const statusColors = {
    pending:      "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30",
    approved:     "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30",
    in_production:"bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/30",
    completed:    "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
    cancelled:    "bg-slate-500/10 text-slate-500 border-slate-500/30",
  };
  const statusTr = { pending:"Bekliyor", approved:"Onaylı", in_production:"Üretimde", completed:"Tamamlandı", cancelled:"İptal" };

  return (
    <button onClick={onClick} data-testid={`label-order-card-${order.id}`}
      className={`w-full text-left rounded-xl border px-3 py-2.5 mb-2 transition-all duration-150 ${
        selected ? "border-[#E5322A] bg-[#E5322A]/8 dark:bg-[#E5322A]/12"
                 : "border-slate-200 dark:border-[#22304A] hover:border-[#E5322A]/40 hover:bg-slate-50 dark:hover:bg-[#1A2540]"}`}>
      <div className="flex items-center justify-between gap-1 mb-1">
        <span className="font-bold text-xs text-slate-800 dark:text-slate-100 font-mono">{ono(order)}</span>
        {order.status && (
          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${statusColors[order.status] || statusColors.pending}`}>
            {statusTr[order.status] || order.status}
          </span>
        )}
      </div>
      <div className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate leading-tight">{order.customerName || "—"}</div>
      <div className="flex items-center gap-1.5 mt-1">
        <Package className="w-3 h-3 text-slate-400 shrink-0" />
        <span className="text-[11px] text-slate-400">{itemCount} kalem</span>
        <span className="text-[10px] text-slate-300 dark:text-slate-600 ml-auto">
          {order.createdAt ? new Date(order.createdAt).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" }) : ""}
        </span>
      </div>
    </button>
  );
}

// ─── Ana Sayfa ─────────────────────────────────────────────────────────────────
export default function LabelPrintPage() {
  const [orders,       setOrders]       = useState([]);
  const [customers,    setCustomers]    = useState({});
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [selectedOrder,setSelectedOrder]= useState(null);
 // TEK ÇATI: etiket sayfasından siparişin diğer evraklarına geçiş (merkezî yazdırma modalı)
 const [printDocType, setPrintDocType] = useState(null);
 const { setTab } = useContext(NavigationCtx);
  const [statusFilter, setStatusFilter] = useState("all");
  const [labelSize,    setLabelSize]    = useState(DEFAULT_SIZE);
  const [customW,      setCustomW]      = useState(49);
  const [customH,      setCustomH]      = useState(20);
  // ── Çift şablon (ürün / kargo ayrı hafıza) ─────────────────────────────
  const [urunTmpl,     setUrunTmpl]     = useState(loadTmpl);
  const [kargoTmpl,    setKargoTmpl]    = useState(loadKargoTmpl);
  const [showGuide,    setShowGuide]    = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [showManualLabel, setShowManualLabel] = useState(false);

  // ── Kargo/Ürün modu & TSPL Kalibrasyon (labelMode ÖNCE tanımlanmalı — tmpl bağımlı) ──
  const [labelMode,       setLabelMode]       = useState("urun"); // "urun" | "kargo"
  const [showTSPLPreview, setShowTSPLPreview] = useState(false);
  const [tsplPreviewText, setTsplPreviewText] = useState("");

  // Aktif şablon (labelMode'a göre ürün veya kargo tmpl)
  const tmpl = labelMode === "kargo" ? kargoTmpl : urunTmpl;
  const handleTmplChange = (next) => {
    if (labelMode === "kargo") { setKargoTmpl(next); saveKargoTmpl(next); }
    else                       { setUrunTmpl(next);  saveTmpl(next); }
  };

  // ── Kopya sayısı (per-item ve kargo) ────────────────────────────────────
  const [copiesMap,    setCopiesMap]    = useState({});
  const [cargoCopies,  setCargoCopies]  = useState(1);
  const getCopies = (idx) => copiesMap[idx] || 1;
  const adjCopies = (idx, delta) => setCopiesMap(prev => ({
    ...prev, [idx]: Math.max(1, Math.min(99, (prev[idx] || 1) + delta)),
  }));

  // ── JSPM yazıcı ──────────────────────────────────────────────────────────
  const jspmState = useJspm();
  const [selectedPrinter, setSelectedPrinter] = useState(() => {
    try { return localStorage.getItem("zen_label_printer") || ""; } catch { return ""; }
  });
  const handlePrinterSelect = (name) => {
    setSelectedPrinter(name);
    try { localStorage.setItem("zen_label_printer", name); } catch {}
  };

  // JSPM WebSocket bağlantısını başlat (tek sefer)
  useEffect(() => { jspm.init(); }, []);

  // Sipariş değişince kopya sayılarını sıfırla
  useEffect(() => {
    setCopiesMap({});
    setCargoCopies(1);
  }, [selectedOrder?.id ?? selectedOrder]);

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get("/orders"), api.get("/customers")])
      .then(([ordRes, custRes]) => {
        const loadedOrders = ordRes.data || [];
        setOrders(loadedOrders);
        const custMap = {};
        (custRes.data || []).forEach(c => { custMap[c.id] = c; });
        setCustomers(custMap);
        // Siparişler yüklenince localStorage'dan ön-seçim kontrol et
        try {
          const preselect = localStorage.getItem("zen_label_preselect_order");
          if (preselect) {
            const found = loadedOrders.find(o => o.id === preselect);
            if (found) setSelectedOrder(found);
            localStorage.removeItem("zen_label_preselect_order");
          }
        } catch {}
      })
      .catch(() => toast.error("Veriler yüklenemedi"))
      .finally(() => setLoading(false));
  }, []);

  const filteredOrders = orders.filter(o => {
    if (statusFilter !== "all" && o.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (o.customerName||"").toLowerCase().includes(q) || (ono(o)||"").toLowerCase().includes(q);
  });

  const items    = selectedOrder?.items || [];
  const customer = selectedOrder ? customers[selectedOrder.customerId] || null : null;

  // ── Yazdırma ─────────────────────────────────────────────────────────────
  const handlePrintTSPL = async (itemsToPrint, copies = 1, overrideOrder = null) => {
    if (!itemsToPrint.length) { toast.error("Yazdırılacak kalem yok"); return; }
    const orderForPrint    = overrideOrder || selectedOrder;
    const customerForPrint = overrideOrder ? null : customer;

    if (!jspmState.connected || !selectedPrinter) {
      if (!jspmState.connected)
        toast.info("JSPM bağlı değil — tarayıcı yazdırması kullanılıyor");
      else
        toast.info("Yazıcı seçin — tarayıcı yazdırması kullanılıyor");
      doPrint(itemsToPrint, orderForPrint, customerForPrint, labelSize, tmpl);
      return;
    }

    let success = 0;
    for (const item of itemsToPrint) {
      const tspl = generateTSPL(item, orderForPrint, customerForPrint, labelSize, tmpl, copies);
      try {
        await jspm.silentPrintRaw(selectedPrinter, tspl);
        success++;
      } catch (e) {
        toast.error(`Yazıcı hatası: ${e.message}`);
        return;
      }
    }
    toast.success(`${success} etiket gönderildi${copies > 1 ? ` (${copies} kopya)` : ""} (TSPL)`);
  };

  const handlePrintAll = () => handlePrintTSPL(items, 1); // tümünde 1 kopya
  const handlePrintOne = (item, idx) => handlePrintTSPL([item], getCopies(idx));

  // ── Manuel Etiket Baskısı ────────────────────────────────────────────────
  const handleManualPrint = (mockItem, mockOrder, copies) => {
    handlePrintTSPL([mockItem], copies, mockOrder);
  };

  // ── Kargo etiketi bas ────────────────────────────────────────────────────
  const handleCargoPrint = async () => {
    if (!selectedOrder) return;
    const tspl = generateCargoTSPL(selectedOrder, customer, kargoTmpl, cargoCopies);
    if (!jspmState.connected || !selectedPrinter) {
      toast.info("JSPM bağlı değil — TSPL paneli açıldı");
      setTsplPreviewText(tspl); setShowTSPLPreview(true);
      return;
    }
    try {
      await jspm.silentPrintRaw(selectedPrinter, tspl);
      toast.success(`Kargo etiketi gönderildi (${cargoCopies} kopya)`);
    } catch (e) { toast.error(`Yazıcı hatası: ${e.message}`); }
  };

  // Ham TSPL string'i doğrudan gönder (TSPL Önizleme modalından)
  const handlePrintRaw = async (tsplString) => {
    if (!jspmState.connected || !selectedPrinter) {
      toast.error("Yazıcı seçili değil veya JSPM bağlı değil"); return;
    }
    try {
      await jspm.silentPrintRaw(selectedPrinter, tsplString);
      toast.success("Gönderildi");
    } catch (e) { toast.error(`Yazıcı hatası: ${e.message}`); }
  };

  const STATUS_OPTS = [
    { value: "all",           label: "Tümü" },
    { value: "pending",       label: "Bekliyor" },
    { value: "approved",      label: "Onaylı" },
    { value: "in_production", label: "Üretimde" },
    { value: "completed",     label: "Tamamlandı" },
  ];

  // Önizleme için örnek kalem (ilk kalem veya boş)
  const sampleItem = items[0] || { product: "Ürün Adı", qty: 1, widthCm: 120, heightCm: 240, options: [] };

  return (
    <div className="flex flex-col h-full gap-4" style={{ minHeight: 0 }}>

      {/* Modaller */}
      {showGuide    && <PrinterGuide onClose={() => setShowGuide(false)} />}
      {showManualLabel && (
        <ManualLabelModal
          onClose={() => setShowManualLabel(false)}
          onPrint={handleManualPrint}
        />
      )}
      {showTSPLPreview && (
        <TSPLPreviewModal
          tspl={tsplPreviewText}
          onClose={() => setShowTSPLPreview(false)}
          onPrint={jspmState.connected && selectedPrinter ? handlePrintRaw : null}
        />
      )}
      {showTemplate && (
        <TemplateDrawer
          tmpl={tmpl}
          onChange={handleTmplChange}
          onClose={() => setShowTemplate(false)}
          sampleItem={sampleItem}
          sampleOrder={selectedOrder || { customerName: "Müşteri Adı", orderNo: "#000" }}
          labelMode={labelMode}
        />
      )}

      {/* ── BAŞLIK ─────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {/* MİNİK GERİ OKU — her sayfada, X'e gerek yok */}
          <button
            data-testid="label-back-orders"
            title="Sipariş listesine dön"
            onClick={() => setTab("orders")}
            className="zen-btn-ghost !text-xs shrink-0">
            <ArrowLeft className="w-4 h-4" /> Siparişlere Dön
          </button>
          <ModuleIcon tab="label_print" icon={Tag} grad="from-[#F59E0B] via-[#D97706] to-[#92400E]" size="w-9 h-9" />
          <div>
            <h2 className="text-2xl font-extrabold tracking-tight text-slate-800 dark:text-slate-100">Etiket Bas</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              TSC TTP-244C · {labelMode === "kargo" ? "100×50mm · GAP 2.60mm" : `${labelSize.w}×${labelSize.h}mm`} ·{" "}
              {jspmState.connected && selectedPrinter
                ? <span className="text-emerald-500 font-semibold">TSPL Direkt</span>
                : <span className="text-amber-500">Tarayıcı fallback</span>}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* ── Manuel Etiket Butonu ─────────────────── */}
          <button
            onClick={() => setShowManualLabel(true)}
            data-testid="open-manual-label-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-[#E5322A]/60 bg-[#E5322A]/5 text-sm font-semibold text-[#E5322A] hover:bg-[#E5322A]/10 transition-all">
            <Tag className="w-3.5 h-3.5" />
            Manuel Etiket
          </button>

          {/* ── Ürün / Kargo Mod Seçici ─────────────── */}
          <div className="flex rounded-xl border border-slate-200 dark:border-[#22304A] overflow-hidden" data-testid="label-mode-selector">
            <button
              onClick={() => setLabelMode("urun")}
              data-testid="mode-urun-btn"
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold transition-all ${
                labelMode === "urun" ? "bg-[#E5322A] text-white" : "bg-white dark:bg-[#141E30] text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[#1A2540]"
              }`}>
              <Tag className="w-3.5 h-3.5" /> Ürün
            </button>
            <button
              onClick={() => setLabelMode("kargo")}
              data-testid="mode-kargo-btn"
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold transition-all border-l border-slate-200 dark:border-[#22304A] ${
                labelMode === "kargo" ? "bg-[#E5322A] text-white" : "bg-white dark:bg-[#141E30] text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[#1A2540]"
              }`}>
              <Truck className="w-3.5 h-3.5" /> Kargo
            </button>
          </div>

          {/* JSPM Yazıcı Durumu + Seçici */}
          <PrinterSelector
            connected={jspmState.connected}
            connecting={jspmState.connecting}
            printers={jspmState.printers}
            selectedPrinter={selectedPrinter}
            onSelect={handlePrinterSelect}
          />

          {/* Tümünü Bas — sadece ürün modunda */}
          {labelMode === "urun" && selectedOrder && items.length > 0 && (
            <button onClick={handlePrintAll} data-testid="label-print-all-btn"
              className="zen-btn-primary flex items-center gap-2">
              <Printer className="w-4 h-4" />
              Tümünü Bas ({items.length})
            </button>
          )}

          {/* Şablon Editörü */}
          <button
            onClick={() => setShowTemplate(v => !v)}
            data-testid="open-template-btn"
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-semibold transition-all ${
              showTemplate
                ? "border-[#E5322A] bg-[#E5322A]/8 text-[#E5322A]"
                : "border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#141E30] text-slate-700 dark:text-slate-200 hover:border-[#E5322A]/50"}`}>
            <Palette className="w-4 h-4" />
            Şablon
          </button>

          {/* Boyut Seçici */}
          <SizeSelector
            size={labelSize}
            customW={customW}
            customH={customH}
            onSelect={s => { setLabelSize(s); if (s.id !== "custom") { setCustomW(s.w); setCustomH(s.h); } }}
            onCustomChange={(dim, val) => dim === "w" ? setCustomW(val) : setCustomH(val)}
          />

          {/* Yazıcı Rehberi */}
          <button
            onClick={() => setShowGuide(true)}
            data-testid="open-guide-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#141E30] text-sm text-slate-600 dark:text-slate-300 hover:border-blue-400 transition-all">
            <HelpCircle className="w-4 h-4 text-blue-500" />
            <span className="hidden sm:inline">Yazıcı Rehberi</span>
          </button>
        </div>
      </div>

      {/* ── ANA İÇERİK ─────────────────────────────────────────── */}
      <div className="flex gap-4 flex-1 min-h-0 overflow-hidden">
        {/* ── SAĞ: Kalemler & Etiketler ────────────────────────── */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {!selectedOrder ? (
            <div className="h-full flex flex-col items-center justify-center gap-4 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-100 to-amber-50 dark:from-amber-900/30 dark:to-amber-800/20 flex items-center justify-center">
                <Tag className="w-8 h-8 text-amber-500" />
              </div>
              <div>
                <p className="font-semibold text-slate-600 dark:text-slate-300">Sipariş seçin</p>
                <p className="text-sm text-slate-400 mt-1">Siparişler sayfasından "Etiket Bas (49×20)" ile açmak en kolayı — ya da aşağıdan arayıp seçin</p>
              </div>
              <div className="w-full max-w-md space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Sipariş no veya müşteri ara..." data-testid="label-search-input"
                    className="zen-input !pl-9 text-sm w-full" />
                </div>
                <div className="flex flex-wrap gap-1 justify-center">
                  {STATUS_OPTS.map(s => (
                    <button key={s.value} onClick={() => setStatusFilter(s.value)}
                      className={`px-2 py-1 rounded-md text-[10px] font-semibold border transition-all ${
                        statusFilter === s.value ? "bg-[#E5322A] text-white border-[#E5322A]"
                        : "border-slate-200 dark:border-[#22304A] text-slate-500 dark:text-slate-400 hover:border-[#E5322A]/40"}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
                <div className="max-h-[38vh] overflow-y-auto rounded-xl border border-slate-200 dark:border-white/10 divide-y divide-slate-100 dark:divide-white/5">
                  {loading ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                  ) : filteredOrders.length === 0 ? (
                    <div className="py-8 text-sm text-slate-400">Sipariş bulunamadı</div>
                  ) : (
                    filteredOrders.map(o => (
                      <OrderCard key={o.id} order={o} selected={false} onClick={() => setSelectedOrder(o)} />
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="zen-panel p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <ChevronRight className="w-4 h-4 text-[#E5322A] shrink-0" />
                  <div className="min-w-0">
                    <div className="font-bold text-slate-800 dark:text-slate-100">{ono(selectedOrder)} — {selectedOrder.customerName}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{items.length} kalem · Adres: {customer?.address || "—"}</div>
                  </div>
                </div>
              </div>

              {/* ── TEK ÇATI: Bu siparişin diğer evrakları (merkezî yazdırma modalına geçiş) ──
                  Şerit yazdırma merkezindekiyle BİREBİR aynıdır: 5 düğme, zen-evrak-btn geometrisi */}
              <div className="zen-panel p-3 flex flex-wrap items-center gap-2" data-testid="label-other-docs">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1 select-none">Bu siparişin diğer evrakları →</span>
                <button onClick={() => setPrintDocType("teklif")} data-testid="label-doc-teklif"
                  className="zen-evrak-btn">
                  <FileText className="w-3.5 h-3.5" /> Teklif / Fatura
                </button>
                <button onClick={() => setPrintDocType("imalat")} data-testid="label-doc-imalat"
                  className="zen-evrak-btn">
                  <Factory className="w-3.5 h-3.5" /> İmalat Fişi
                </button>
                <button onClick={() => setPrintDocType("toptanci")} data-testid="label-doc-toptanci"
                  className="zen-evrak-btn">
                  <Receipt className="w-3.5 h-3.5" /> Toptancı Sipariş
                </button>
                <button onClick={() => setPrintDocType("etiket")} data-testid="label-doc-etiket"
                  className="zen-evrak-btn">
                  <Printer className="w-3.5 h-3.5" /> Etiket (70×40)
                </button>
                {/* Küçük Etiket Bas — zaten bu sayfada: tıklanınca sayfa yenilenmez, akış devam eder */}
                <button
                  data-testid="label-doc-kucuk-etiket"
                  title="Etiket Bas sayfasındasınız — küçük etiketler bu sayfada basılır"
                  aria-pressed="true"
                  onClick={() => setPrintDocType(null)}
                  className="zen-evrak-btn"
                >
                  <Tag className="w-3.5 h-3.5" />
                  Küçük Etiket Bas
                </button>
              </div>

              {/* ── KARGO MOD paneli ─────────────────────────────────── */}
              {labelMode === "kargo" && (
                <div className="zen-panel p-5 flex items-start justify-between gap-4" data-testid="cargo-label-panel">
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                        <Truck className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <div className="font-bold text-slate-800 dark:text-slate-100">Kargo Etiketi</div>
                        <div className="text-[11px] text-slate-400">100×50mm · GAP 2.60mm · Yan 1.3mm</div>
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate">{selectedOrder.customerName}</div>
                    {customer?.address && (
                      <div className="flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                        <MapPin className="w-3 h-3 shrink-0 mt-0.5 text-slate-400" />{customer.address}
                      </div>
                    )}
                    {(customer?.city || customer?.district) && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 pl-4">
                        {[customer.district, customer.city, customer.country].filter(Boolean).join(" / ")}
                      </div>
                    )}
                    {(customer?.phone || customer?.telephone) && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 pl-4">
                        Tel: {customer.phone || customer.telephone}
                      </div>
                    )}
                    <div className="text-[11px] text-slate-400 pl-4">{items.length} kalem</div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button
                      data-testid="cargo-tspl-preview-btn"
                      onClick={() => {
                        const t = generateCargoTSPL(selectedOrder, customer, kargoTmpl, cargoCopies);
                        setTsplPreviewText(t); setShowTSPLPreview(true);
                      }}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-[#22304A] text-xs font-semibold text-slate-600 dark:text-slate-300 hover:border-[#E5322A]/50 bg-white dark:bg-[#141E30] transition-all">
                      <Eye className="w-3.5 h-3.5" /> TSPL Gör
                    </button>
                    <div className="flex items-center gap-2">
                      <CopySelector
                        value={cargoCopies}
                        onDec={() => setCargoCopies(v => Math.max(1, v - 1))}
                        onInc={() => setCargoCopies(v => Math.min(99, v + 1))}
                        testId="cargo-copy-selector"
                      />
                      <button
                        data-testid="cargo-print-btn"
                        onClick={handleCargoPrint}
                        className="zen-btn-primary flex items-center gap-1.5 flex-1 justify-center">
                        <Truck className="w-3.5 h-3.5" /> Kargo Bas
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── ÜRÜN MOD kalemleri ───────────────────────────────── */}
              {labelMode === "urun" && (items.length === 0 ? (
                <div className="zen-panel p-8 text-center text-sm text-slate-400">Bu siparişte kalem bulunamadı</div>
              ) : (
                items.map((item, i) => {
                  const fabric = getFabric(item);
                  const { w, h } = getDims(item);
                  const dir  = getDirection(item);
                  const dims = w && h ? `${w} × ${h} cm` : w ? `${w} cm` : "—";
                  const ralOpt = (item.options||[]).find(o => (o.label||"").toLowerCase().includes("ral"));

                  return (
                    <div key={item.id || i} data-testid={`label-item-row-${i}`}
                      className="zen-panel p-4 flex items-center gap-4 transition-all hover:border-[#E5322A]/30">
                      <div className="w-7 h-7 rounded-full bg-[#E5322A]/10 text-[#E5322A] text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-slate-800 dark:text-slate-100 truncate">{item.product||"—"}{dir ? ` · ${dir}` : ""}</div>
                        {fabric && <div className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">{fabric}</div>}
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">{dims}</span>
                          <span className="text-xs text-slate-400">Adet: {item.qty||1}</span>
                          {ralOpt && <span className="text-[10px] bg-slate-100 dark:bg-[#1A2540] text-slate-500 px-1.5 py-0.5 rounded font-mono">RAL {ralOpt.value}</span>}
                        </div>
                      </div>
                      <LabelPreview item={item} order={selectedOrder} customer={customer} size={labelSize} tmpl={tmpl} />
                      {/* TSPL Gör (kalibrasyon) */}
                      <button
                        data-testid={`tspl-preview-btn-${i}`}
                        title="TSPL komutlarını gör (kalibrasyon)"
                        onClick={() => {
                          const t = generateTSPL(item, selectedOrder, customer, labelSize, tmpl, getCopies(i));
                          setTsplPreviewText(t); setShowTSPLPreview(true);
                        }}
                        className="p-2 rounded-lg border border-slate-200 dark:border-[#22304A] text-slate-400 hover:border-[#E5322A]/40 hover:text-[#E5322A] shrink-0 transition-all">
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      {/* Kopya Seçici */}
                      <CopySelector
                        value={getCopies(i)}
                        onDec={() => adjCopies(i, -1)}
                        onInc={() => adjCopies(i, +1)}
                        testId={`copy-selector-${i}`}
                      />
                      <button onClick={() => handlePrintOne(item, i)} data-testid={`label-print-btn-${i}`}
                        className="zen-btn-primary shrink-0 flex items-center gap-1.5 !px-4 !py-2">
                        <Printer className="w-3.5 h-3.5" />
                        Bas
                      </button>
                    </div>
                  );
                })
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── TEK ÇATI: diğer evraklar (merkezî yazdırma modalı) ── */}
      {printDocType && selectedOrder && (
        <PrintPreviewModal order={selectedOrder} initialDocType={printDocType} onClose={() => setPrintDocType(null)} />
      )}
    </div>
  );
}
